
import { useState } from "react";
import MeetingScheduler from "@/components/MeetingScheduler";
import { useAuthState } from "@/hooks/use-auth-state";
import LoadingScreen from "@/components/ui/loading-screen";
import MainContent from "@/components/meeting/MainContent";
import { usePromptHandler } from "@/hooks/use-prompt-handler";
import { AuthFlow } from "@/components/auth/AuthFlow";
import { safeRemoveItem, safeGetItem, safeSetItem } from "@/utils/storage";
import { parsePrompt } from "@/utils/promptParser";
import { useToast } from "@/hooks/use-toast";

const Index = () => {
  const [meetingComponent, setMeetingComponent] = useState<JSX.Element | null>(null);
  const { toast } = useToast();
  
  const {
    isLoading,
    prompt,
    feedback,
    pendingMeeting,
    setPrompt,
    handleSubmit,
    processPrompt
  } = usePromptHandler();

  const handleSavedPrompt = async (savedPrompt: string | null) => {
    if (!savedPrompt) return;
    
    try {
      console.log('Processing saved prompt:', savedPrompt);
      setPrompt(savedPrompt);
      await processPrompt(savedPrompt);
    } catch (error) {
      console.error('Error processing saved prompt:', error);
      toast({
        title: "Error",
        description: "Failed to process meeting details",
        variant: "destructive"
      });
    } finally {
      safeRemoveItem('pendingMeetingPrompt');
    }
  };

  const { isAuthenticated, userEmail, isInitialized } = useAuthState(handleSavedPrompt);

  const handleGoogleSignIn = async () => {
    try {
      console.log('Starting Google Sign In with prompt:', prompt);
      
      if (pendingMeeting) {
        console.log('Saving prompt before auth:', prompt);
        safeSetItem('pendingMeetingPrompt', prompt);
      }
      
      const authFlow = AuthFlow({
        pendingMeeting,
        prompt,
        onSignOut: handleSignOut,
        setIsLoading: () => {}
      });
      
      await authFlow.handleGoogleSignInClick();
    } catch (error) {
      console.error('Error during Google Sign In:', error);
      toast({
        title: "Authentication Error",
        description: "Failed to sign in with Google",
        variant: "destructive"
      });
    }
  };

  const handleSignOut = () => {
    setPrompt("");
    setMeetingComponent(null);
    window.location.reload();
  };

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (meetingComponent) {
    return meetingComponent;
  }

  return (
    <MainContent
      isAuthenticated={isAuthenticated}
      userEmail={userEmail}
      isLoading={isLoading}
      prompt={prompt}
      feedback={feedback}
      onPromptChange={setPrompt}
      onSignOut={handleSignOut}
      onGoogleSignIn={handleGoogleSignIn}
      onSchedule={handleSubmit}
      parsedMeeting={pendingMeeting}
    />
  );
};

export default Index;
